import { Check } from "@/icons/Check.tsx";
import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { CircleUser } from "@/icons/CircleUser.tsx";
import { Coin } from "@/icons/Coin.tsx";
import { CreditCard } from "@/icons/CreditCard.tsx";
import { FileInvoiceDollar } from "@/icons/FileInvoiceDollar.tsx";
import { Gem } from "@/icons/Gem.tsx";
import { SubscriptionStatus } from "@/models/subscription.model.ts";
import useUserStore from "@/store/user.store.ts";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Group,
  List,
  SegmentedControl,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

type TabType = {
  value: "monthly" | "yearly";
  label: string;
};

const tabs: TabType[] = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

type PlanPricing = {
  monthly: number;
  yearly: number;
};

type Plan = {
  slug: string;
  label: string;
  credits: number;
  pricing: PlanPricing;
  photos: number;
  storage: {
    monthly: string;
    yearly: string;
  };
};

const plans: Plan[] = [
  {
    slug: "starter",
    label: "Starter",
    credits: 2500,
    photos: 250,
    storage: {
      monthly: "30 days",
      yearly: "Lifetime unlimited",
    },
    pricing: {
      monthly: 19,
      yearly: 190,
    },
  },
  {
    slug: "basic",
    label: "Basic",
    credits: 5500,
    photos: 550,
    storage: {
      monthly: "Unlimited",
      yearly: "Lifetime unlimited",
    },
    pricing: {
      monthly: 39,
      yearly: 390,
    },
  },
  {
    slug: "pro",
    label: "Pro",
    credits: 14000,
    photos: 1400,
    storage: {
      monthly: "Unlimited",
      yearly: "Lifetime unlimited",
    },
    pricing: {
      monthly: 99,
      yearly: 990,
    },
  },
];

export function Billing() {
  const { user, hasActiveSubscription, getActiveSubscriptions } =
    useUserStore();
  const [type, setType] = useState<TabType["value"]>("monthly");
  const hasActiveSub = hasActiveSubscription();
  const subscriptions = getActiveSubscriptions();
  const [coupon, setCoupon] = useState<string>("");
  const [searchParams] = useSearchParams();

  const webUrl = import.meta.env.VITE_WEB_URL;
  const subscriptionColor = function (status: SubscriptionStatus) {
    if (status === "canceled") {
      return "red";
    }

    if (status === "active") {
      return "green";
    }

    return "black";
  };

  useEffect(() => {
    const couponCode = searchParams.get("coupon");

    if (couponCode) {
      setCoupon(couponCode);
    }
  }, [setCoupon, searchParams]);

  return (
    <Container
      size="xl"
      px={0}
      className="grid grid-cols-1 lg:grid-cols-6 gap-5"
    >
      <div className="relative flex flex-col col-span-1 lg:col-span-2 gap-3">
        {user?.premium == true && (
          <div className="absolute right-2 top-2">
            <Tooltip
              label="Your photos remain protected and won’t be deleted, even without an active subscription"
              withArrow
            >
              <Badge color="lime" leftSection={<Gem size={10} />}>
                Premium
              </Badge>
            </Tooltip>
          </div>
        )}
        <Group wrap="nowrap">
          <Avatar src={user?.avatar} size={80} radius="md">
            <CircleUser size={64} />
          </Avatar>
          <div>
            <Text fz="lg" fw={500}>
              {user?.name}
            </Text>

            <Group wrap="nowrap" gap={10} mt={3}>
              <Text fz="sm" c="dimmed">
                {user?.email}
              </Text>
            </Group>
          </div>
        </Group>

        {user && (
          <Card withBorder p="md" radius="md">
            <Group justify="space-between">
              <Group>
                <Coin size={22} />
                <Text size="md">Credits</Text>
              </Group>

              <Text size="md">
                {new Intl.NumberFormat().format(user.credits)}
              </Text>
            </Group>
          </Card>
        )}

        {subscriptions?.map((subscription) => (
          <Card
            key={subscription.id}
            component="a"
            href={`${webUrl}/portal/${user?.uuid}`}
            withBorder
            p="md"
            radius="md"
          >
            <Card.Section withBorder={true} p="md">
              <Group justify="space-between">
                <Group>
                  <CreditCard size={22} />
                  <Text size="md">Subscription</Text>
                </Group>

                <Badge fw="normal" variant="dot" size="lg">
                  {subscription.name?.replace("_", " ").toUpperCase()}
                </Badge>
              </Group>
            </Card.Section>
            <Card.Section withBorder={true} p="md">
              <Group justify="space-between">
                <Group>
                  <CircleInfo size={22} />
                  <Text size="md">Status</Text>
                </Group>

                <Badge
                  color={subscriptionColor(subscription.status)}
                  fw="normal"
                  variant="dot"
                  size="lg"
                >
                  {subscription.status.replace("_", " ").toUpperCase()}
                </Badge>
              </Group>
            </Card.Section>
            <Card.Section withBorder={true} p="md">
              <Group justify="space-between">
                <Group>
                  <CircleInfo size={22} />
                  <Text size="md">Started at</Text>
                </Group>

                <Badge fw="normal" variant="dot" size="lg">
                  {subscription.starts_at}
                </Badge>
              </Group>
            </Card.Section>
            {subscription.cancel_at_period_end && (
              <Card.Section withBorder={true} p="md">
                <Alert
                  bd="1px dashed"
                  color="indigo"
                  icon={<CircleInfo size={20} />}
                >
                  Your subscription will be canceled at {subscription.ends_at}!
                </Alert>
              </Card.Section>
            )}
          </Card>
        ))}

        <div className="flex gap-2">
          <Button
            component="a"
            href={`${webUrl}/portal/${user?.uuid}`}
            size="md"
            variant="default"
            fullWidth={true}
            leftSection={<FileInvoiceDollar />}
          >
            Invoices
          </Button>

          {hasActiveSub && (
            <Button
              component="a"
              href={`${webUrl}/portal/${user?.uuid}`}
              size="md"
              variant="default"
              fullWidth={true}
            >
              View subscriptions
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col col-span-1 lg:col-span-4 gap-8">
        {coupon && (
          <Alert bd="1px dashed" color="green" icon={<span>🎁</span>}>
            Use promo code <b>{coupon}</b> on checkout to get a 15% discount on
            your first purchase!
          </Alert>
        )}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Title order={3} fw={500}>
              Credits management
            </Title>

            <Divider />
          </div>

          <Alert bd="1px dashed" color="indigo" icon={<CircleInfo size={20} />}>
            You can top-up your account with credits at any time.
          </Alert>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card withBorder p="md" radius="md" shadow="sm">
              <Card.Section withBorder inheritPadding py="xs">
                <div className="flex flex-col gap-2">
                  <div className="text-xl">
                    {new Intl.NumberFormat().format(1000)} credits
                  </div>
                </div>
              </Card.Section>

              <Card.Section withBorder inheritPadding py="xs">
                <div className="flex py-2 items-baseline">
                  <div className="text-4xl">$9</div>
                </div>

                <List
                  spacing="xs"
                  size="sm"
                  mt="md"
                  center
                  icon={<Check size={16} />}
                >
                  <List.Item>Around 100 photos</List.Item>
                </List>
              </Card.Section>

              <Card.Section withBorder inheritPadding py="xs">
                <Button
                  component="a"
                  target="_blank"
                  href={`${webUrl}/checkout/${user?.uuid}/top-up/1000`}
                  fullWidth
                >
                  Buy
                </Button>
              </Card.Section>
            </Card>

            <Card withBorder p="md" radius="md" shadow="sm">
              <Card.Section withBorder inheritPadding py="xs">
                <div className="flex flex-col gap-2">
                  <div className="text-xl">
                    {new Intl.NumberFormat().format(2200)} credits
                  </div>
                </div>
              </Card.Section>

              <Card.Section withBorder inheritPadding py="xs">
                <div className="flex py-2 items-baseline">
                  <div className="text-4xl">$19</div>
                </div>

                <List
                  spacing="xs"
                  size="sm"
                  mt="md"
                  center
                  icon={<Check size={16} />}
                >
                  <List.Item>Around 220 photos</List.Item>
                </List>
              </Card.Section>

              <Card.Section withBorder inheritPadding py="xs">
                <Button
                  component="a"
                  target="_blank"
                  href={`${webUrl}/checkout/${user?.uuid}/top-up/2200`}
                  fullWidth
                >
                  Buy
                </Button>
              </Card.Section>
            </Card>

            <Card withBorder p="md" radius="md" shadow="sm">
              <Card.Section withBorder inheritPadding py="xs">
                <div className="flex flex-col gap-2">
                  <div className="text-xl">
                    {new Intl.NumberFormat().format(5000)} credits
                  </div>
                </div>
              </Card.Section>

              <Card.Section withBorder inheritPadding py="xs">
                <div className="flex py-2 items-baseline">
                  <div className="text-4xl">$39</div>
                </div>

                <List
                  spacing="xs"
                  size="sm"
                  mt="md"
                  center
                  icon={<Check size={16} />}
                >
                  <List.Item>Around 500 photos</List.Item>
                </List>
              </Card.Section>

              <Card.Section withBorder inheritPadding py="xs">
                <Button
                  component="a"
                  target="_blank"
                  href={`${webUrl}/checkout/${user?.uuid}/top-up/5000`}
                  fullWidth
                >
                  Buy
                </Button>
              </Card.Section>
            </Card>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Title order={3} fw={500}>
              Subscription management
            </Title>

            <Divider />
          </div>

          <div className="w-full flex items-center justify-center">
            <SegmentedControl
              value={type}
              onChange={(value) => setType(value as TabType["value"])}
              size="md"
              data={tabs}
              radius="md"
            />
          </div>

          <Alert bd="1px dashed" color="indigo" icon={<CircleInfo size={20} />}>
            You can have multiple active subscriptions at the same time.
          </Alert>

          {user?.premium === false && (
            <Alert bd="1px dashed" color="green" icon={<Gem size={20} />}>
              Gain lifetime unlimited storage history by activating a annual
              subscription.
            </Alert>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {plans.map((plan: Plan) => (
              <Card key={plan.label} withBorder p="md" radius="md" shadow="sm">
                <Card.Section withBorder inheritPadding py="xs">
                  <div className="flex flex-col gap-2">
                    <div className="text-xl">{plan.label}</div>
                  </div>
                </Card.Section>

                <Card.Section withBorder inheritPadding py="xs">
                  <div className="flex py-2 items-baseline">
                    <div className="text-4xl">${plan.pricing[type]}</div>
                    <span>/{type === "monthly" ? "month" : "year"}</span>
                  </div>

                  <List
                    spacing="xs"
                    size="sm"
                    mt="md"
                    center
                    icon={<Check size={16} />}
                  >
                    <List.Item>
                      {new Intl.NumberFormat().format(
                        plan.credits * (type === "monthly" ? 1 : 12),
                      )}{" "}
                      credits per {type === "monthly" ? "month" : "year"}
                    </List.Item>
                    <List.Item>
                      Around{" "}
                      {new Intl.NumberFormat().format(
                        plan.photos * (type === "monthly" ? 1 : 12),
                      )}{" "}
                      photos per {type === "monthly" ? "month" : "year"}
                    </List.Item>
                    <List.Item>
                      {plan.storage[type]} photo storage history
                    </List.Item>
                  </List>
                </Card.Section>

                <Card.Section withBorder inheritPadding py="xs">
                  <Button
                    component="a"
                    target="_blank"
                    href={`${webUrl}/checkout/${user?.uuid}/${plan.slug}/${type}`}
                    fullWidth
                  >
                    Activate
                  </Button>
                </Card.Section>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Container>
  );
}
